<script lang="ts">
	import type { Document } from "../../../core/schema/Document.js";
	import DocumentCard from "./DocumentCard.svelte";
	import DocumentsShowMore from "./DocumentsShowMore.svelte";
	import type { DocumentCategory } from "../../../core/schema/DocumentCategory.js";
	import { slugify } from "../../../core/utils/slugify.js";
	import { nbspify } from "../../../core/utils/nbspify.js";

	export let title: string;
	export let documents: Document[];
	export let category: DocumentCategory;
	export let alwaysShowAll = false as boolean;

	$: subcategories = new Set(
		documents.map(({ subcategory }) => subcategory).filter((value): value is string => value !== undefined),
	);
	function getSubcategoryGroups(documentsOfSubcategory: Document[]): Set<string> {
		return new Set(
			documentsOfSubcategory
				.map(({ subcategoryGroup }) => subcategoryGroup)
				.filter((value): value is string => value !== undefined),
		);
	}

	const beforeShowMoreAmount = alwaysShowAll ? Infinity : 4;
	let showMoreMap: Record<string, boolean> = {};

	function toggleShowMore(subcategory?: string, subcategoryGroup?: string): void {
		let key = "_all";
		if (subcategory) {
			key = subcategory;
			if (subcategoryGroup) {
				key += `-${subcategoryGroup}`;
			}
		}

		showMoreMap = {
			...showMoreMap,
			[key]: !(showMoreMap[key] ?? false),
		};
	}
</script>

<div class="pt-60 lg:pt-80">
	<div id={slugify(category)} class="flex flex-col">
		<h2 class="m-0 px-[2.375rem] pb-16 text-2xl font-semibold">{nbspify(title)}</h2>
		{#if subcategories.size > 0}
			<div class="grid auto-cols-fr auto-rows-fr gap-x-5 gap-y-2 lg:grid-flow-col">
				{#each subcategories as subcategory}
					<a
						href="#sub-{slugify(subcategory)}"
						class="bg-tertiary lg:text-2lg flex min-h-28 flex-col justify-end px-11 py-6 text-[1.75rem] font-bold leading-relaxed transition-colors hover:bg-white lg:h-full lg:py-3.5 lg:leading-snug"
					>
						{nbspify(subcategory)}
					</a>
				{/each}
			</div>
		{/if}
	</div>
	{#if subcategories.size > 0}
		{#each subcategories as subcategory}
			{@const documentsOfSubcategory = documents.filter((document) => document.subcategory === subcategory)}
			{@const subcategoryGroups = getSubcategoryGroups(documentsOfSubcategory)}

			<div id="sub-{slugify(subcategory)}" class="pt-24 lg:pt-36">
				<h3 class="lg:text-2lg m-0 p-11 text-[1.75rem] font-bold">{nbspify(subcategory)}</h3>
				{#if subcategoryGroups.size > 0}
					<div class="flex flex-col gap-32">
						{#each subcategoryGroups as subcategoryGroup}
							{@const documentsOfSubcategoryGroup = documentsOfSubcategory.filter(
								(document) => document.subcategoryGroup === subcategoryGroup,
							)}
							{@const showMore = showMoreMap[`${subcategory}-${subcategoryGroup}`]}
							<div>
								<h4 class="lg:text-2lg bg-tertiary m-0 p-11 text-[1.75rem] font-bold">{nbspify(subcategoryGroup)}</h4>
								{#each documentsOfSubcategoryGroup.slice(0, showMore ? undefined : beforeShowMoreAmount) as document}
									<DocumentCard {document} />
								{/each}
								<DocumentsShowMore
									{beforeShowMoreAmount}
									documents={documentsOfSubcategoryGroup}
									{showMore}
									on:toggle={() => {
										toggleShowMore(subcategory, subcategoryGroup);
									}}
								/>
							</div>
						{/each}
					</div>
				{:else}
					{@const showMore = showMoreMap[subcategory]}
					{#each documentsOfSubcategory.slice(0, showMore ? undefined : beforeShowMoreAmount) as document}
						<DocumentCard {document} />
					{/each}
					<DocumentsShowMore
						{beforeShowMoreAmount}
						documents={documentsOfSubcategory}
						{showMore}
						on:toggle={() => {
							toggleShowMore(subcategory);
						}}
					/>
				{/if}
			</div>
		{/each}
	{:else}
		{@const showMore = showMoreMap._all}
		{#each documents.slice(0, showMore ? undefined : beforeShowMoreAmount) as document}
			<DocumentCard {document} />
		{/each}
		<DocumentsShowMore
			{beforeShowMoreAmount}
			{documents}
			{showMore}
			on:toggle={() => {
				toggleShowMore();
			}}
		/>
	{/if}
</div>
